<script lang="ts">
  import LedSceneBuilderDialog from './LedSceneBuilderDialog.svelte';
  import { inputDevicesQuery } from '$lib/queries/input_devices';
  import { ledScenesQuery } from '$lib/queries/led_scenes';
  import { deleteLedSceneMutation } from '$lib/mutations/led_scenes';
  import { ledLogicCanvasUI } from '$lib/state/ui/ledLogicCanvas.svelte';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api';
  import {
    buildLedSceneIndexes,
    formatLedTargets,
    getDeviceTypeLabel,
    keyHasLedScene,
    ledSceneForKey,
    toDomainGraphScenes
  } from '$lib/utils/led_scene_index';
  import type { InputDeviceResponse } from '$lib/types/input_device';
  import type { InputKeyResponse } from '$lib/types/input_key';
  import type { LEDLoad } from '$lib/types/led_scene';

  type DeviceUi = InputDeviceResponse & { name?: string };

  let { id }: { id: number } = $props();

  const devices = inputDevicesQuery();
  const ledScenes = ledScenesQuery();
  const deleteScene = deleteLedSceneMutation();

  const device = $derived((devices.data ?? []).find((d) => d.id === id) as DeviceUi | undefined);
  const allDevices = $derived((devices.data ?? []) as DeviceUi[]);

  const indexes = $derived(
    buildLedSceneIndexes(toDomainGraphScenes(ledScenes.data ?? []), allDevices)
  );

  const keys = $derived(
    (device?.keys ?? []).sort((a, b) => (a.key_index ?? 0) - (b.key_index ?? 0))
  );

  const isCorridor = $derived(device?.type === 'corridor_keypad');

  const selectedKey = $derived(
    keys.find((k) => k.id === ledLogicCanvasUI.selectedKeyId) ?? keys[0] ?? null
  );

  $effect(() => {
    if (keys.length && !keys.some((k) => k.id === ledLogicCanvasUI.selectedKeyId)) {
      ledLogicCanvasUI.selectedKeyId = keys[0]?.id ?? null;
    }
  });

  const busy = $derived(deleteScene.isPending);

  function keyName(key: InputKeyResponse) {
    return key?.name?.trim() ? key.name : `Key ${(key.key_index ?? 0) + 1}`;
  }

  function actionFor(key: InputKeyResponse, eventType: string) {
    return (key.actions ?? []).find((a) => a.event_type === eventType);
  }

  function openBuilder(eventType: 'on' | 'off') {
    if (!isCorridor && !actionFor(selectedKey!, eventType)) {
      toast.error('Program this button’s scene action in Keypads first');
      return;
    }
    builderEvent = eventType;
    const scene = selectedKey ? ledSceneForKey(selectedKey, eventType, indexes) : undefined;
    builderExistingSceneId = scene?.id ?? null;
    builderExistingLoads = scene?.led_loads ?? [];
    ledLogicCanvasUI.showLedSceneForm = true;
  }

  let builderEvent = $state<'on' | 'off'>('on');
  let builderExistingSceneId = $state<number | null>(null);
  let builderExistingLoads = $state<LEDLoad[]>([]);

  async function clearMapping(key: InputKeyResponse, eventType: 'on' | 'off') {
    const scene = ledSceneForKey(key, eventType, indexes);
    if (!scene) return;
    try {
      await deleteScene.mutateAsync(scene.id);
      toast.success('LED mapping cleared');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }
</script>

<div class="p-3 space-y-3">
  <div class="flex items-center justify-between gap-3">
    <div>
      <h2 class="text-base font-medium text-text-primary">
        {device?.name ?? `Device ${id}`}
      </h2>
      {#if device}
        <p class="text-xs text-text-muted">
          {getDeviceTypeLabel(device)}
          {#if device.keys[0]?.address != null}
            · Address {device.keys[0].address}
          {/if}
        </p>
      {/if}
    </div>
  </div>

  {#if keys.length === 0}
    <p class="text-sm text-text-muted">This device has no keys.</p>
  {:else}
    <div class="grid grid-cols-[auto_1fr] gap-6">
      <div class="flex flex-col items-center gap-3">
        <div
          class="rounded-2xl p-3 flex flex-col gap-2 w-[150px] shadow-toyama-md bg-[#1b1b1d] border border-[#3a3a3f]"
          role="listbox"
          aria-label="Device buttons"
        >
          {#each keys as key (key.id)}
            {@const isSelected = key.id === ledLogicCanvasUI.selectedKeyId}
            {@const mapped = keyHasLedScene(key, indexes)}
            <button
              type="button"
              role="option"
              aria-selected={isSelected}
              class="relative rounded-md min-h-[44px] px-2 py-1.5 flex items-center justify-center text-center transition-shadow duration-100 text-[#f4f4f5] bg-[#2a2a2e]"
              style="border:1px solid {isSelected ? '#97D700' : '#45454c'}; box-shadow:{isSelected
                ? '0 0 0 2px rgba(151,215,0,0.45)'
                : 'none'}"
              onclick={() => (ledLogicCanvasUI.selectedKeyId = key.id)}
              title={keyName(key)}
            >
              <span class="text-xs font-medium leading-tight line-clamp-2">
                {keyName(key)}
              </span>
              {#if mapped}
                <span
                  class="absolute top-1 right-1 h-1.5 w-1.5 rounded-full"
                  style="background:#97D700"
                  title="LED mapping configured"
                ></span>
              {/if}
            </button>
          {/each}
        </div>
        <div class="text-2xs text-text-muted">
          {keys.length}-button
        </div>
      </div>

      {#if selectedKey}
        <div class="rounded-md border border-border-default p-4 space-y-4 self-start">
          <div>
            <div class="text-2xs uppercase tracking-wide text-text-muted mb-1">
              Button {(selectedKey.key_index ?? 0) + 1}
            </div>
            <p class="text-sm text-text-primary">{keyName(selectedKey)}</p>
          </div>

          {#each [{ ev: 'on' as const, label: 'On action (press)' }, { ev: 'off' as const, label: 'Off action (release / second press)' }] as row (row.ev)}
            {@const act = actionFor(selectedKey, row.ev)}
            {@const scene = ledSceneForKey(selectedKey, row.ev, indexes)}
            <div>
              <div class="text-2xs uppercase tracking-wide text-text-muted mb-1">
                {row.label}
              </div>
              <div class="flex items-start gap-2">
                <div class="flex-1 min-w-0">
                  {#if !act && !isCorridor}
                    <p class="text-sm text-text-muted">Scene action not set</p>
                    <p class="text-2xs text-text-muted mt-0.5">
                      Program in Keypads before mapping LEDs.
                    </p>
                  {:else if !scene}
                    <p class="text-sm text-text-muted">Not mapped</p>
                  {:else}
                    <p class="text-sm text-text-primary">
                      {formatLedTargets(scene.led_loads, allDevices)}
                    </p>
                    <p class="text-2xs text-text-muted mt-0.5">
                      {#if scene.is_manually_edited}
                        <span class="text-state-warn">Manual</span>
                      {/if}
                      {#if scene.is_stale}
                        {#if scene.is_manually_edited} · {/if}
                        <span class="text-state-error">Stale</span>
                      {/if}
                    </p>
                  {/if}
                </div>
                <button
                  type="button"
                  class="btn-secondary text-xs shrink-0"
                  disabled={busy || (!isCorridor && !act)}
                  onclick={() => openBuilder(row.ev)}
                  title={!isCorridor && !act ? 'Program scene action in Keypads first' : undefined}
                >
                  {scene ? 'Change…' : 'Set…'}
                </button>
                {#if scene}
                  <button
                    type="button"
                    class="text-xs text-state-error hover:underline shrink-0"
                    disabled={busy}
                    onclick={() => clearMapping(selectedKey, row.ev)}
                  >
                    Clear
                  </button>
                {/if}
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</div>

<LedSceneBuilderDialog
  open={ledLogicCanvasUI.showLedSceneForm}
  device={device ?? null}
  keyId={selectedKey?.id ?? null}
  eventType={builderEvent}
  existingSceneId={builderExistingSceneId}
  existingLoads={builderExistingLoads}
  buttonLabel={selectedKey ? keyName(selectedKey) : ''}
  onclose={() => (ledLogicCanvasUI.showLedSceneForm = false)}
  onsaved={() => (ledLogicCanvasUI.showLedSceneForm = false)}
/>
